import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, AlertTriangle, Ban } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ScheduledPostStatus } from "@content-os/config";
import { getStatusColor } from "./platform-colors";

const STATUS_ICON: Record<ScheduledPostStatus, LucideIcon> = {
  SCHEDULED: Clock,
  PUBLISHED: CheckCircle2,
  FAILED: AlertTriangle,
  CANCELLED: Ban,
};

/**
 * Status pill for a calendar event, colored by ScheduledPostStatus (shared
 * palette in platform-colors.ts). `compact` renders just the colored icon for
 * dense chips; the full form adds a tinted label pill.
 */
export function EventStatusBadge({
  status,
  compact = false,
}: {
  status: ScheduledPostStatus;
  compact?: boolean;
}) {
  const { t } = useTranslation("calendar");
  const color = getStatusColor(status);
  const Icon = STATUS_ICON[status] ?? Clock;
  const label = t(`status.${status}`, {
    defaultValue: status.charAt(0) + status.slice(1).toLowerCase(),
  });

  if (compact) {
    return (
      <Icon
        size={12}
        strokeWidth={2.5}
        style={{ color }}
        role="img"
        aria-label={label}
        className="flex-shrink-0"
      />
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
      style={{ color, backgroundColor: `${color}1A` }}
    >
      <Icon size={12} />
      {label}
    </span>
  );
}
